/**
 * Build-time app config (Vite import.meta.env), baked into the bundle.
 *
 * Replaces the old runtime fetch('/env.json') + window.__ENV__ pattern: the
 * values were always fixed at build time, so fetching them at runtime only
 * added a render-blocking round-trip and a white-screen failure mode.
 *
 * Sources: apps/seeker/.env.local, written by `node scripts/generate-env.js
 * <stage> seeker` (local dev reads env/seeker.<stage>.json; CI/Cloudflare
 * Pages reads the ENV_JSON_SEEKER variable). All values here are
 * public-by-design — they ship in browser JS either way.
 *
 * window.__ENV__ is still assigned (synchronously) from this object in
 * main.tsx as a transitional shim for existing read sites (incl. env.ts
 * ensureEnv()). New code should import { config } from this module instead.
 */
import type { SeekerEnv } from './env';

export type SeekerConfig = SeekerEnv & {
  STRIPE_PUBLISHABLE_KEY?: string;
  POSTHOG_PUBLIC_KEY?: string;
  POSTHOG_PUBLIC_HOST?: string;
  SITE_URL?: string;
  SEEKER_URL?: string;
};

const env = import.meta.env;

export const config: SeekerConfig = {
  STAGE: env.VITE_STAGE ?? '',
  SUPABASE_URL: env.VITE_SUPABASE_URL ?? '',
  SUPABASE_ANON_KEY: env.VITE_SUPABASE_ANON_KEY ?? '',
  EDGE_BASE: env.VITE_EDGE_BASE,
  SSE_URL: env.VITE_SSE_URL,
  STRIPE_PUBLISHABLE_KEY: env.VITE_STRIPE_PUBLISHABLE_KEY,
  POSTHOG_PUBLIC_KEY: env.VITE_POSTHOG_PUBLIC_KEY,
  POSTHOG_PUBLIC_HOST: env.VITE_POSTHOG_PUBLIC_HOST,
  SITE_URL: env.VITE_SITE_URL,
  SEEKER_URL: env.VITE_SEEKER_URL,
};

const required = ['STAGE', 'SUPABASE_URL', 'SUPABASE_ANON_KEY'] as const;
const missing = required.filter((k) => !config[k]);
if (missing.length > 0) {
  // A build-time mistake (generate-env.js also guards this); fail loudly.
  throw new Error(
    `Missing required build-time config: ${missing.join(', ')}. ` +
      'Run `node scripts/generate-env.js <stage> seeker` before building.'
  );
}

declare global {
  interface Window {
    /** Transitional shim — assigned from `config` in main.tsx. Prefer importing { config }. */
    __ENV__: SeekerConfig;
  }
}
